import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { Users2, Ticket, CheckCircle2, XCircle, DollarSign, ShieldCheck } from "lucide-react";

function Stat({ label, value, icon: Icon, testid, accent }) {
  return (
    <div data-testid={testid} className="border border-border bg-card p-5">
      <div className="flex items-center justify-between mb-3">
        <span className="text-[11px] uppercase tracking-[0.15em] text-muted-foreground">{label}</span>
        <Icon className={`h-4 w-4 ${accent ? "text-primary" : "text-muted-foreground"}`} />
      </div>
      <div className="font-display text-4xl">{value}</div>
    </div>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);

  useEffect(() => {
    api.get("/stats").then(({ data }) => setStats(data)).catch(() => {});
  }, []);

  const money = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}`;

  return (
    <div>
      <div className="mb-8">
        <h1 className="text-4xl">Good day, {user?.name.split(" ")[0]}.</h1>
        <p className="text-muted-foreground mt-1">Here is the state of the ledger.</p>
      </div>

      {!stats ? (
        <p className="font-mono text-sm text-muted-foreground">Loading figures…</p>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-px bg-border border border-border">
          <div className="contents">
            <Stat testid="stat-customers" label="Total Customers" value={stats.total_customers} icon={Users2} accent />
            <Stat testid="stat-converted" label="Converted" value={stats.converted} icon={CheckCircle2} />
            <Stat testid="stat-not-converted" label="Not Converted" value={stats.not_converted} icon={XCircle} />
            <Stat testid="stat-active-tickets" label="Active Tickets" value={stats.active_tickets} icon={Ticket} accent />
          </div>
          {(user?.role === "admin" || user?.role === "sales") && (
            <Stat testid="stat-sales" label="Total Sales" value={money(stats.total_sales)} icon={DollarSign} accent />
          )}
          <Stat testid="stat-closed-tickets" label="Closed Tickets" value={stats.closed_tickets} icon={Ticket} />
          {user?.role === "admin" && (
            <Stat testid="stat-users" label="Team Members" value={stats.total_users} icon={ShieldCheck} />
          )}
        </div>
      )}

      {stats && (user?.role === "admin" || user?.role === "sales") && stats.sale_status_breakdown && (
        <div className="mt-8">
          <h2 className="text-lg mb-3">Sale Status Breakdown</h2>
          <div className="grid grid-cols-3 gap-px bg-border border border-border max-w-md">
            {Object.entries(stats.sale_status_breakdown).map(([k, v]) => (
              <div key={k} className="bg-card p-4 text-center" data-testid={`breakdown-${k.toLowerCase()}`}>
                <div className="font-display text-2xl">{v}</div>
                <div className="text-xs uppercase tracking-wide text-muted-foreground">{k}</div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
